/**
 * Field Reconciliation Wizard (Feature 35)
 *
 * Step 4 of the seller's-pack flow. Walks the seller through every required
 * TA6/TA7/LPE1 field with state badges, source attribution, and
 * Bedrock-rewritten conveyancing-language for free-text answers.
 *
 * Design principles (per .kiro/steering/design-system-components.md):
 *   - Plain English. Procedural tone. No urgency.
 *   - One emerald-700 CTA per screen.
 *   - WCAG AA contrast; 44px touch targets; visible focus rings.
 *   - State badges use color AND text (no color-only encoding).
 *   - Original/rewritten side-by-side requires explicit user choice — never
 *     auto-confirm a Bedrock rewrite.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiClientError, ReconciliationField } from '../utils/api';

type FormType = 'TA6' | 'TA7' | 'LPE1';

interface Props {
  packId: string;
  onAllConfirmed?: () => void;
}

interface SectionGroup {
  section: string;
  fields: ReconciliationField[];
}

interface FormGroup {
  formType: FormType;
  total: number;
  confirmed: number;
  sections: SectionGroup[];
}

interface RewriteState {
  fieldId: string;
  original: string;
  rewritten: string;
  factualDeltas: string[];
}

const FORM_LABEL: Record<FormType, string> = {
  TA6: 'TA6 — Property Information',
  TA7: 'TA7 — Leasehold Information',
  LPE1: 'LPE1 — Leasehold Property Enquiries',
};

function StateBadge({ state }: { state: ReconciliationField['state'] }) {
  // Color + text + symbol. Color is decorative only — text carries the state.
  switch (state) {
    case 'confirmed':
      return (
        <span className="inline-flex items-center gap-1 rounded-sm border-l-4 border-emerald-700 bg-emerald-50 px-2 py-1 text-sm font-medium text-emerald-900">
          <span aria-hidden="true">✓</span>Confirmed
        </span>
      );
    case 'auto_filled':
      return (
        <span className="inline-flex items-center gap-1 rounded-sm border-l-4 border-emerald-700 bg-white px-2 py-1 text-sm font-medium text-slate-900">
          Auto-filled
        </span>
      );
    case 'needs_review':
      return (
        <span className="inline-flex items-center gap-1 rounded-sm border-l-4 border-amber-500 bg-amber-50 px-2 py-1 text-sm font-medium text-slate-900">
          Needs review
        </span>
      );
    case 'needs_user_input':
      return (
        <span className="inline-flex items-center gap-1 rounded-sm border-l-4 border-slate-400 bg-slate-50 px-2 py-1 text-sm font-medium text-slate-900">
          Needs your answer
        </span>
      );
  }
}

/**
 * Group fields first by form, then by section, preserving the order in
 * which the API returned them so the agent's section ordering carries
 * through to the UI.
 */
function groupFields(fields: ReconciliationField[]): FormGroup[] {
  const formOrder: FormType[] = ['TA6', 'TA7', 'LPE1'];
  const byForm = new Map<FormType, Map<string, ReconciliationField[]>>();

  for (const field of fields) {
    const formMap = byForm.get(field.formType) ?? new Map();
    const sectionList = formMap.get(field.section) ?? [];
    sectionList.push(field);
    formMap.set(field.section, sectionList);
    byForm.set(field.formType, formMap);
  }

  const groups: FormGroup[] = [];
  for (const formType of formOrder) {
    const formMap = byForm.get(formType);
    if (!formMap) continue;
    const sections: SectionGroup[] = Array.from(formMap.entries()).map(
      ([section, sectionFields]) => ({ section, fields: sectionFields })
    );
    const flat = sections.flatMap((s) => s.fields);
    const total = flat.filter((f) => f.required).length;
    const confirmed = flat.filter((f) => f.required && f.state === 'confirmed').length;
    groups.push({ formType, total, confirmed, sections });
  }
  return groups;
}

/**
 * Single field card. Owns the local Edit / Rewrite text input state so the
 * parent doesn't have to track every textarea separately.
 */
function FieldCard({
  field,
  packId,
  onUpdated,
  rewriteState,
  onStartRewrite,
  onClearRewrite,
}: {
  field: ReconciliationField;
  packId: string;
  onUpdated: (next: ReconciliationField) => void;
  rewriteState: RewriteState | null;
  onStartRewrite: (state: RewriteState) => void;
  onClearRewrite: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(field.value ?? '');
  const [rewriteInput, setRewriteInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const handleAccept = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.confirmField(packId, field.fieldId, {
        action: 'accept',
      });
      onUpdated(result.field);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not confirm answer');
    } finally {
      setBusy(false);
    }
  }, [packId, field.fieldId, onUpdated]);

  const handleSaveEdit = useCallback(async () => {
    if (!editValue.trim()) {
      setError('Please enter an answer before saving.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await api.confirmField(packId, field.fieldId, {
        action: 'edit',
        value: editValue.trim(),
      });
      onUpdated(result.field);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save edit');
    } finally {
      setBusy(false);
    }
  }, [packId, field.fieldId, editValue, onUpdated]);

  const handleRequestRewrite = useCallback(async () => {
    if (!rewriteInput.trim()) {
      setError('Please write your answer in plain English first.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await api.rewriteField(packId, field.fieldId, rewriteInput.trim());
      onStartRewrite({
        fieldId: field.fieldId,
        original: result.original,
        rewritten: result.rewritten,
        factualDeltas: result.factualDeltas,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not generate rewrite');
    } finally {
      setBusy(false);
    }
  }, [packId, field.fieldId, rewriteInput, onStartRewrite]);

  const handleAcceptRewriteChoice = useCallback(
    async (choice: 'original' | 'rewritten') => {
      if (!rewriteState) return;
      setBusy(true);
      setError(null);
      try {
        const value =
          choice === 'original' ? rewriteState.original : rewriteState.rewritten;
        const result = await api.confirmField(packId, field.fieldId, {
          action: 'rewrite',
          value,
          originalValue: rewriteState.original,
        });
        onUpdated(result.field);
        onClearRewrite();
        setRewriteInput('');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not confirm answer');
      } finally {
        setBusy(false);
      }
    },
    [packId, field.fieldId, rewriteState, onUpdated, onClearRewrite]
  );

  const isConfirmed = field.state === 'confirmed';
  const showRewriteUI = rewriteState?.fieldId === field.fieldId;
  const hasFactualDeltas =
    showRewriteUI && rewriteState!.factualDeltas.length > 0;

  return (
    <div
      className="border border-slate-300 bg-white p-4 sm:p-6 rounded-sm"
      data-testid={`field-card-${field.fieldId}`}
    >
      <div className="flex flex-wrap items-start gap-3 justify-between">
        <h4 className="text-base sm:text-lg font-semibold text-slate-900 flex-1 min-w-0">
          {field.questionPlainEnglish}
        </h4>
        <StateBadge state={field.state} />
      </div>

      {/* Existing value + source */}
      {field.value && (field.state === 'auto_filled' || field.state === 'needs_review' || isConfirmed) && (
        <div className="mt-3">
          <div className="text-sm text-slate-700 font-medium">Current answer:</div>
          <p className="text-base text-slate-900 mt-1 whitespace-pre-wrap break-words">
            {field.value}
          </p>
          {field.sourceDocumentName && (
            <p className="text-sm text-slate-700 mt-2">
              Source: {field.sourceDocumentName}
              {field.sourcePageRef ? ` (${field.sourcePageRef})` : ''}
              {field.confidence !== null && (
                <span className="ml-2 text-slate-700">
                  · {Math.round(field.confidence * 100)}% confidence
                </span>
              )}
            </p>
          )}
        </div>
      )}

      {/* Editing UI */}
      {editing && !isConfirmed && (
        <div className="mt-4">
          <label
            htmlFor={`edit-${field.fieldId}`}
            className="block text-sm font-medium text-slate-900 mb-1"
          >
            Your answer
          </label>
          <textarea
            id={`edit-${field.fieldId}`}
            className="w-full border border-slate-300 rounded-sm p-3 text-base focus:outline focus:outline-3 focus:outline-yellow-300 focus:outline-offset-0 min-h-[88px]"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            disabled={busy}
            data-testid={`field-edit-input-${field.fieldId}`}
          />
        </div>
      )}

      {/* Free-text input for needs_user_input fields */}
      {field.state === 'needs_user_input' && !showRewriteUI && (
        <div className="mt-4">
          <label
            htmlFor={`rewrite-${field.fieldId}`}
            className="block text-sm font-medium text-slate-900 mb-1"
          >
            Your answer (we will rewrite it for you in conveyancing language)
          </label>
          <textarea
            id={`rewrite-${field.fieldId}`}
            className="w-full border border-slate-300 rounded-sm p-3 text-base focus:outline focus:outline-3 focus:outline-yellow-300 focus:outline-offset-0 min-h-[88px]"
            placeholder="Write in plain English."
            value={rewriteInput}
            onChange={(e) => setRewriteInput(e.target.value)}
            disabled={busy}
            data-testid={`field-rewrite-input-${field.fieldId}`}
          />
        </div>
      )}

      {/* Rewrite results — original vs rewritten with explicit choice */}
      {showRewriteUI && (
        <div className="mt-4 space-y-3" data-testid={`field-rewrite-result-${field.fieldId}`}>
          {hasFactualDeltas ? (
            <div className="border-l-4 border-amber-500 bg-amber-50 p-3 rounded-sm">
              <p className="text-sm font-medium text-slate-900 mb-1">
                Your answer needs clarification before we can rewrite it.
              </p>
              <ul className="text-sm text-slate-900 list-disc pl-5 space-y-1">
                {rewriteState!.factualDeltas.map((d, i) => (
                  <li key={i}>{d}</li>
                ))}
              </ul>
              <button
                type="button"
                className="btn-secondary mt-3"
                onClick={() => {
                  onClearRewrite();
                }}
              >
                Edit my answer
              </button>
            </div>
          ) : (
            <>
              <p className="text-sm font-medium text-slate-900">
                Choose which version to use in your pack:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="border border-slate-300 bg-slate-50 p-3 rounded-sm">
                  <div className="text-sm font-medium text-slate-900 mb-1">
                    Your answer
                  </div>
                  <p className="text-base text-slate-900 whitespace-pre-wrap break-words">
                    {rewriteState!.original}
                  </p>
                  <button
                    type="button"
                    className="btn-secondary mt-3 w-full"
                    disabled={busy}
                    onClick={() => handleAcceptRewriteChoice('original')}
                    data-testid={`field-pick-original-${field.fieldId}`}
                  >
                    Use my answer
                  </button>
                </div>
                <div className="border border-emerald-700 bg-white p-3 rounded-sm">
                  <div className="text-sm font-medium text-slate-900 mb-1">
                    Conveyancing version
                  </div>
                  <p className="text-base text-slate-900 whitespace-pre-wrap break-words">
                    {rewriteState!.rewritten}
                  </p>
                  <button
                    type="button"
                    className="btn-primary mt-3 w-full"
                    disabled={busy}
                    onClick={() => handleAcceptRewriteChoice('rewritten')}
                    data-testid={`field-pick-rewritten-${field.fieldId}`}
                  >
                    Use rewritten version
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {error && (
        <div
          role="alert"
          className="mt-3 border-l-4 border-red-600 bg-red-50 p-3 rounded-sm text-sm text-red-700"
          data-testid={`field-error-${field.fieldId}`}
        >
          {error}
        </div>
      )}

      {/* Action row */}
      {!isConfirmed && !showRewriteUI && (
        <div className="mt-4 flex flex-wrap gap-2">
          {!editing && field.state !== 'needs_user_input' && (
            <>
              <button
                type="button"
                className="btn-primary"
                disabled={busy}
                onClick={handleAccept}
                data-testid={`field-accept-${field.fieldId}`}
              >
                Confirm
              </button>
              <button
                type="button"
                className="btn-secondary"
                disabled={busy}
                onClick={() => {
                  setEditing(true);
                  setEditValue(field.value ?? '');
                }}
                data-testid={`field-edit-${field.fieldId}`}
              >
                Edit
              </button>
            </>
          )}
          {editing && (
            <>
              <button
                type="button"
                className="btn-primary"
                disabled={busy}
                onClick={handleSaveEdit}
                data-testid={`field-save-edit-${field.fieldId}`}
              >
                Save edit
              </button>
              <button
                type="button"
                className="btn-secondary"
                disabled={busy}
                onClick={() => {
                  setEditing(false);
                  setEditValue(field.value ?? '');
                  setError(null);
                }}
              >
                Cancel
              </button>
            </>
          )}
          {field.state === 'needs_user_input' && (
            <button
              type="button"
              className="btn-primary"
              disabled={busy || !rewriteInput.trim()}
              onClick={handleRequestRewrite}
              data-testid={`field-request-rewrite-${field.fieldId}`}
            >
              Rewrite for solicitor
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function FieldReconciliationWizard({ packId, onAllConfirmed }: Props) {
  const [fields, setFields] = useState<ReconciliationField[] | null>(null);
  const [progress, setProgress] = useState<{ confirmed: number; total: number }>({
    confirmed: 0,
    total: 0,
  });
  const [loadError, setLoadError] = useState<string | null>(null);
  const [rewriteState, setRewriteState] = useState<RewriteState | null>(null);

  const refresh = useCallback(async () => {
    setLoadError(null);
    try {
      const result = await api.listFields(packId);
      setFields(result.fields);
      setProgress(result.progress);
    } catch (err) {
      const msg =
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Could not load your answers';
      setLoadError(msg);
    }
  }, [packId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const groups = useMemo(() => (fields ? groupFields(fields) : []), [fields]);

  const handleFieldUpdated = useCallback(
    (next: ReconciliationField) => {
      setFields((prev) =>
        prev ? prev.map((f) => (f.fieldId === next.fieldId ? next : f)) : prev
      );
      // Recompute progress optimistically; refresh in background to catch
      // any server-side recomputation we missed.
      void refresh();
    },
    [refresh]
  );

  // Notify the parent when the pack is fully confirmed (so it can enable
  // "Continue to payment").
  useEffect(() => {
    if (
      progress.total > 0 &&
      progress.confirmed === progress.total &&
      onAllConfirmed
    ) {
      onAllConfirmed();
    }
  }, [progress, onAllConfirmed]);

  if (loadError) {
    return (
      <div
        role="alert"
        className="border-l-4 border-red-600 bg-red-50 p-4 rounded-sm"
        data-testid="reconciliation-wizard-error"
      >
        <p className="text-base text-red-700 font-medium">{loadError}</p>
        <button type="button" className="btn-secondary mt-3" onClick={() => void refresh()}>
          Retry
        </button>
      </div>
    );
  }

  if (!fields) {
    return (
      <div className="text-base text-slate-700" data-testid="reconciliation-wizard-loading">
        Loading your answers…
      </div>
    );
  }

  if (fields.length === 0) {
    return (
      <div className="border border-slate-300 bg-white p-4 rounded-sm">
        <p className="text-base text-slate-900">
          We couldn't find any fields to review yet. If you've just uploaded
          documents, please give us a moment to process them.
        </p>
      </div>
    );
  }

  const percentage =
    progress.total === 0
      ? 0
      : Math.round((progress.confirmed / progress.total) * 100);

  return (
    <div className="space-y-6" data-testid="reconciliation-wizard">
      {/* Overall progress */}
      <div className="border border-slate-300 bg-white p-4 rounded-sm">
        <div className="flex items-baseline justify-between">
          <h3 className="text-lg font-semibold text-slate-900">Pack review</h3>
          <div
            className="text-base text-slate-900 font-medium"
            data-testid="reconciliation-wizard-progress"
            aria-live="polite"
          >
            {progress.confirmed} of {progress.total} confirmed ({percentage}%)
          </div>
        </div>
        <div
          className="mt-2 h-2 bg-slate-200 rounded-sm overflow-hidden"
          role="progressbar"
          aria-valuenow={percentage}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="h-full bg-emerald-700 transition-all"
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>

      {/* Per-form sections */}
      {groups.map((group) => (
        <section key={group.formType} className="space-y-4">
          <div className="flex flex-wrap items-baseline gap-3 justify-between">
            <h3 className="text-xl font-semibold text-slate-900">
              {FORM_LABEL[group.formType]}
            </h3>
            <span className="text-sm text-slate-700">
              {group.confirmed} of {group.total} confirmed
            </span>
          </div>
          {group.sections.map((sec) => (
            <div key={`${group.formType}-${sec.section}`} className="space-y-3">
              <h4 className="text-base font-semibold text-slate-900">
                {sec.section}
              </h4>
              {sec.fields.map((field) => (
                <FieldCard
                  key={field.fieldId}
                  field={field}
                  packId={packId}
                  onUpdated={handleFieldUpdated}
                  rewriteState={rewriteState}
                  onStartRewrite={setRewriteState}
                  onClearRewrite={() => setRewriteState(null)}
                />
              ))}
            </div>
          ))}
        </section>
      ))}
    </div>
  );
}
